import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Loader2, X, Image as ImageIcon, Share2, Trash2, ZoomIn, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { AuthThumb } from './AuthThumb';

// Full-size blobs cached for the session so swiping back (and sharing) is instant.
const fullCache = new Map(); // path -> { url, blob }
const MAX_FULL = 40;
const MIN_SCALE = 1;
const MAX_SCALE = 5;

async function loadFull(api, path) {
  if (fullCache.has(path)) return fullCache.get(path);
  const r = await api.get(path, { responseType: 'blob', timeout: 120000 });
  const hit = { url: URL.createObjectURL(r.data), blob: r.data };
  fullCache.set(path, hit);
  while (fullCache.size > MAX_FULL) {
    const [k, v] = fullCache.entries().next().value;
    URL.revokeObjectURL(v.url);
    fullCache.delete(k);
  }
  return hit;
}

/** Lightbox items for a diary entry's SharePoint photos (deletable by author/admin). */
export const entryPhotoItems = (entryId, photos = []) => photos
  .filter(p => p?.id)
  .map(p => ({
    key: p.id,
    entryId,
    photoId: p.id,
    name: p.name || 'Photo',
    full: `/diary/entries/${entryId}/photos/${p.id}/thumb?size=c1600x1600`,
    thumb: `/diary/entries/${entryId}/photos/${p.id}/thumb`,
    webUrl: p.web_url,
    // Live link by item id — still right after the file is moved/tidied.
    linkPath: `/diary/entries/${entryId}/photos/${p.id}/link`,
    deletable: true,
  }));

/** Lightbox items for a Building Control capture's stamped photos (never deletable here). */
export const bcPhotoItems = (captureId, photos = []) => photos.map((p, i) => ({
  key: p.id,
  name: `Photo ${i + 1}${p.sent ? ' · sent' : ''}`,
  full: `/bc/captures/${captureId}/photo/${p.id}?variant=${p.sent ? 'sent' : 'stamped'}`,
  thumb: `/bc/captures/${captureId}/photo/${p.id}?thumb=1`,
  badge: p.sent ? 'SENT' : null,
  deletable: false,
}));

const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * Full-screen photo viewer for the Site Diary — swipe / arrows / keyboard
 * between shots, pinch (or wheel / double-tap) to zoom and drag to pan,
 * share via the phone's share sheet, bin for a wrong shot (author/admin).
 * `items` come from entryPhotoItems / bcPhotoItems (authenticated API paths).
 */
export function DiaryPhotoLightbox({ items, index, onIndexChange, onClose, title, canDelete = false, onDelete }) {
  const { api } = useAuth();
  const item = items[index];
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState({ s: 1, x: 0, y: 0 });
  const [gesturing, setGesturing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const stageRef = useRef(null);
  const stripRef = useRef(null);
  const g = useRef({ mode: null, lastTap: 0 }); // live gesture bookkeeping (no re-renders)

  const zoomed = zoom.s > 1.01;
  const go = (delta) => { if (items.length > 1) onIndexChange((index + delta + items.length) % items.length); };
  const resetZoom = () => setZoom({ s: 1, x: 0, y: 0 });

  // Keep the pan inside the stage so the photo can't be dragged out of sight.
  const clampPan = (s, x, y) => {
    const el = stageRef.current;
    if (!el) return { s, x, y };
    const maxX = ((s - 1) * el.clientWidth) / 2;
    const maxY = ((s - 1) * el.clientHeight) / 2;
    return { s, x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
  };

  // Zoom about a stage point (px from stage centre) so what's under the finger stays put.
  const zoomAt = (nextScale, px, py, base = zoom) => {
    const s = clamp(nextScale, MIN_SCALE, MAX_SCALE);
    if (s === 1) { setZoom({ s: 1, x: 0, y: 0 }); return; }
    const k = s / base.s;
    setZoom(clampPan(s, px - (px - base.x) * k, py - (py - base.y) * k));
  };

  const stagePoint = (clientX, clientY) => {
    const r = stageRef.current.getBoundingClientRect();
    return { px: clientX - r.left - r.width / 2, py: clientY - r.top - r.height / 2 };
  };

  useEffect(() => {
    if (!item) return undefined;
    let alive = true;
    resetZoom();
    setConfirmDelete(false);
    setSrc(fullCache.get(item.full)?.url || null);
    setFailed(false);
    loadFull(api, item.full).then(h => { if (alive) setSrc(h.url); }).catch(() => { if (alive) setFailed(true); });
    // Warm the neighbours so a swipe lands on a ready image.
    [items[index + 1], items[index - 1]].forEach(n => { if (n) loadFull(api, n.full).catch(() => {}); });
    stripRef.current?.querySelector(`[data-idx="${index}"]`)?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    return () => { alive = false; };
  }, [api, item, items, index]);

  useEffect(() => {
    const onKey = (e) => {
      if (confirmDelete) return;
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === '0') resetZoom();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ── Touch: 1 finger = swipe (unzoomed) or pan (zoomed); 2 fingers = pinch; double-tap toggles 2.5× ──
  const onTouchStart = (e) => {
    const t = e.touches;
    if (t.length === 2) {
      const mid = stagePoint((t[0].clientX + t[1].clientX) / 2, (t[0].clientY + t[1].clientY) / 2);
      g.current = { ...g.current, mode: 'pinch', d0: dist(t[0], t[1]), z0: zoom, ...mid };
      setGesturing(true);
      return;
    }
    const now = Date.now();
    if (now - g.current.lastTap < 300) {
      const { px, py } = stagePoint(t[0].clientX, t[0].clientY);
      zoomed ? resetZoom() : zoomAt(2.5, px, py);
      g.current = { ...g.current, mode: null, lastTap: 0 };
      return;
    }
    g.current = { ...g.current, mode: zoomed ? 'pan' : 'swipe', lastTap: now, x0: t[0].clientX, y0: t[0].clientY, z0: zoom, moved: false };
    if (zoomed) setGesturing(true);
  };
  const onTouchMove = (e) => {
    const t = e.touches;
    const cur = g.current;
    if (cur.mode === 'pinch' && t.length === 2) {
      zoomAt(cur.z0.s * (dist(t[0], t[1]) / cur.d0), cur.px, cur.py, cur.z0);
    } else if (cur.mode === 'pan' && t.length === 1) {
      const dx = t[0].clientX - cur.x0; const dy = t[0].clientY - cur.y0;
      if (Math.abs(dx) + Math.abs(dy) > 3) cur.moved = true;
      setZoom(clampPan(cur.z0.s, cur.z0.x + dx, cur.z0.y + dy));
    }
  };
  const onTouchEnd = (e) => {
    const cur = g.current;
    if (cur.mode === 'swipe' && e.changedTouches.length) {
      const dx = e.changedTouches[0].clientX - cur.x0;
      if (Math.abs(dx) > 50) { go(dx < 0 ? 1 : -1); cur.lastTap = 0; }
    }
    if (cur.mode === 'pinch' && e.touches.length === 1) {
      // Second finger lifted — carry on as a pan with the remaining one.
      g.current = { ...cur, mode: 'pan', x0: e.touches[0].clientX, y0: e.touches[0].clientY, z0: zoom };
      return;
    }
    g.current.mode = null;
    setGesturing(false);
  };

  // ── Mouse / trackpad: wheel zooms, drag pans when zoomed, double-click toggles ──
  const onWheel = (e) => {
    const { px, py } = stagePoint(e.clientX, e.clientY);
    zoomAt(zoom.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15), px, py);
  };
  const onMouseDown = (e) => {
    if (!zoomed || e.button !== 0) return;
    g.current = { ...g.current, mode: 'mousepan', x0: e.clientX, y0: e.clientY, z0: zoom };
    setGesturing(true);
  };
  const onMouseMove = (e) => {
    const cur = g.current;
    if (cur.mode !== 'mousepan') return;
    setZoom(clampPan(cur.z0.s, cur.z0.x + (e.clientX - cur.x0), cur.z0.y + (e.clientY - cur.y0)));
  };
  const onMouseUp = () => { if (g.current.mode === 'mousepan') { g.current.mode = null; setGesturing(false); } };
  const onDoubleClick = (e) => { const { px, py } = stagePoint(e.clientX, e.clientY); zoomed ? resetZoom() : zoomAt(2.5, px, py); };

  // ── Share: phone share sheet with the actual file; desktop falls back to download ──
  const handleShare = async () => {
    const hit = fullCache.get(item.full);
    if (!hit) { toast.info('Wait for the photo to finish loading'); return; }
    const type = hit.blob.type || 'image/jpeg';
    const name = /\.(jpe?g|png|webp|heic)$/i.test(item.name) ? item.name : `${item.name}.jpg`;
    const file = new File([hit.blob], name, { type });
    setSharing(true);
    try {
      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share({ files: [file], title: title || item.name, text: title ? `${title} — ${item.name}` : item.name });
      } else {
        const a = document.createElement('a');
        a.href = hit.url; a.download = name; a.rel = 'noopener';
        document.body.appendChild(a); a.click(); a.remove();
        toast.success('Photo downloaded');
      }
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('Sharing not available on this device');
    } finally {
      setSharing(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(item);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  // SharePoint link: resolve by item id first (files may have been tidied into
  // another folder since the entry was written), else the stored URL.
  const openInSharePoint = async () => {
    let url = item.webUrl;
    if (item.linkPath) {
      try { url = (await api.get(item.linkPath)).data?.url || url; } catch { /* stored link */ }
    }
    if (url) window.open(url, '_blank', 'noopener');
    else toast.error('No SharePoint link for this photo');
  };

  if (!item) return null;
  const showDelete = canDelete && item.deletable && !!onDelete;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-[100vw] w-[100vw] h-[100dvh] sm:max-w-[96vw] sm:w-[96vw] sm:h-[94vh] p-0 gap-0 bg-[#0B0E13] border-white/10 flex flex-col overflow-hidden rounded-none sm:rounded-lg [&>button]:hidden"
        data-testid="diary-photo-lightbox"
      >
        <DialogTitle className="sr-only">{item.name}</DialogTitle>
        <DialogDescription className="sr-only">Swipe or use the arrows to move between photos. Pinch to zoom.</DialogDescription>

        <div className="flex items-center gap-1 px-3 py-2.5 border-b border-white/10 pt-safe">
          <div className="min-w-0 flex-1 pr-2">
            <div className="text-sm font-medium text-white truncate" data-testid="diary-lightbox-name">{title || item.name}</div>
            <div className="text-[11px] text-gray-500 truncate" data-testid="diary-lightbox-counter">
              {index + 1} of {items.length}{title ? ` · ${item.name}` : ''}{zoomed ? ` · ${zoom.s.toFixed(1)}×` : ''}
            </div>
          </div>
          {zoomed && (
            <button type="button" onClick={resetZoom} className="p-2 rounded-md text-[#F5A623] hover:text-white" title="Reset zoom" data-testid="diary-lightbox-reset-zoom">
              <ZoomIn className="w-4 h-4" />
            </button>
          )}
          <button type="button" onClick={handleShare} disabled={sharing || !src} className="p-2 rounded-md text-gray-300 hover:text-white disabled:opacity-40" title={navigator.share ? 'Share photo' : 'Download photo'} data-testid="diary-lightbox-share">
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : navigator.share ? <Share2 className="w-4 h-4" /> : <Download className="w-4 h-4" />}
          </button>
          {(item.webUrl || item.linkPath) && (
            <button type="button" onClick={openInSharePoint} className="p-2 rounded-md text-gray-400 hover:text-white" title="Open in SharePoint" data-testid="diary-lightbox-sharepoint">
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
          {showDelete && (
            <button type="button" onClick={() => setConfirmDelete(true)} className="p-2 rounded-md text-gray-300 hover:text-red-400" title="Remove this photo" data-testid="diary-lightbox-delete">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button type="button" onClick={onClose} className="p-2 rounded-md text-gray-300 hover:text-white" aria-label="Close" data-testid="diary-lightbox-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div
          ref={stageRef}
          className="relative flex-1 min-h-0 flex items-center justify-center select-none overflow-hidden"
          style={{ touchAction: 'none', cursor: zoomed ? (gesturing ? 'grabbing' : 'grab') : 'default' }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onDoubleClick={onDoubleClick}
          data-testid="diary-lightbox-stage"
        >
          {failed && (
            <div className="text-center text-sm text-gray-400" data-testid="diary-lightbox-error">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-60" />
              Couldn&apos;t load this photo{item.webUrl ? ' — try Open in SharePoint.' : '.'}
            </div>
          )}
          {!failed && !src && <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" data-testid="diary-lightbox-loading" />}
          {!failed && src && (
            <img
              key={item.key}
              src={src}
              alt={item.name}
              className="max-w-full max-h-full object-contain will-change-transform"
              style={{
                transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})`,
                transition: gesturing ? 'none' : 'transform 160ms ease-out',
              }}
              draggable={false}
              data-testid="diary-lightbox-image"
              data-zoom={zoom.s.toFixed(2)}
            />
          )}
          {item.badge && (
            <span className="absolute top-3 left-3 text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-600 text-white">{item.badge}</span>
          )}
          {items.length > 1 && !zoomed && (
            <>
              <button type="button" onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/60 text-white hover:bg-black/80" aria-label="Previous" data-testid="diary-lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
              <button type="button" onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/60 text-white hover:bg-black/80" aria-label="Next" data-testid="diary-lightbox-next"><ChevronRight className="w-6 h-6" /></button>
            </>
          )}
          {!zoomed && !failed && src && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10.5px] text-gray-500 pointer-events-none">
              Pinch or double-tap to zoom
            </div>
          )}

          {confirmDelete && (
            <div className="absolute inset-0 bg-black/70 flex items-end sm:items-center justify-center p-4" data-testid="diary-lightbox-delete-confirm">
              <div className="w-full max-w-sm rounded-xl bg-[#1E2530] border border-white/10 p-4">
                <div className="text-sm font-semibold text-white">Remove this photo?</div>
                <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                  It comes off this entry and the file is moved to the OneDrive recycle bin (admins can restore it there for 93 days).
                </p>
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting} className="flex-1 py-2 rounded-md text-sm text-gray-300 bg-white/5 hover:bg-white/10" data-testid="diary-lightbox-delete-cancel">Keep</button>
                  <button type="button" onClick={handleDelete} disabled={deleting} className="flex-1 py-2 rounded-md text-sm font-semibold text-white bg-red-600 hover:bg-red-500 inline-flex items-center justify-center gap-1.5" data-testid="diary-lightbox-delete-confirm-btn">
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Remove
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {items.length > 1 && (
          <div ref={stripRef} className="flex gap-1.5 px-3 py-2 overflow-x-auto border-t border-white/10 bg-black/40 pb-safe" data-testid="diary-lightbox-strip">
            {items.map((it, i) => (
              <button
                key={it.key}
                type="button"
                data-idx={i}
                onClick={() => onIndexChange(i)}
                className={`w-14 h-14 flex-shrink-0 rounded-md overflow-hidden border-2 ${i === index ? 'border-[#F5A623]' : 'border-transparent opacity-60 hover:opacity-100'}`}
                title={it.name}
                data-testid={`diary-lightbox-thumb-${i}`}
              >
                <AuthThumb endpoint={it.thumb} testKey={`lb-${it.key}`} placeholderIcon={ImageIcon} alt={it.name} className="w-full h-full" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DiaryPhotoLightbox;
